import Immutable from 'immutable';
import { type MediaSourceData, VideoData } from '@lumen5/luminary';

import Crunker from 'crunker';
import type {
    AIAvatar,
    AIAvatarVoicePreset,
    AssetContainerModel,
    AudioAssetModel,
    BatchTextToVoiceoverResponse,
    SlideBackgroundModel,
    TranscriptWord,
} from 'js/src/client';
import { AssetContainerTypes, SlideBackgroundModelTypeEnum, SourceEnum } from 'js/src/client';
import type { ScriptSentenceData } from 'js/src/data/ai-voices-and-avatars/VoiceoverCache';
import { VoiceoverCache } from 'js/src/data/ai-voices-and-avatars/VoiceoverCache';
import { type FileWithMetadata, immutableAssetContainerModelIsAudio } from 'js/src/data/uploads';
import { BatchAnalytics } from 'js/src/libs/BatchAnalytics';
import type { ImmutableMap } from 'js/src/libs/consts';
import { UNKNOWN_LANGUAGE_CODE, WORD_DURATION_ESTIMATE_SECONDS } from 'js/src/libs/consts';
import { createAbortController } from 'js/src/libs/createAbortController';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA } from 'js/src/libs/scene-utils';
import UploadMediaController from 'js/src/libs/upload-media-controller';
import uuid from 'js/src/libs/uuid';

/**
 * Reads audio duration from a path/url and caches it unto the given key in session storage
 */
export function cacheVoiceoverDuration(key: string, path: string) {
    const audioElement = new Audio(path);
    audioElement.addEventListener('loadeddata', () => {
        try {
            sessionStorage.setItem(key, audioElement.duration.toFixed(2));
        } catch (error) {
            // The cache is probably full, we should clear it and try again.
            sessionStorage.clear();
            sessionStorage.setItem(key, audioElement.duration.toFixed(2));
        }
    });
}

/**
 * Generate AI voiceover for a given text + voice pair
 * Responses are cached in the session storage to avoid re-fetching the same text + voice pair
 */
export function convertTextToVoiceover(
    text: string,
    voice_preset: string,
    regenerate = false,
    language_code?: string,
) {
    return new Promise<string>((resolve, reject) => {
        let shouldFetch = true;
        const cache = new VoiceoverCache();
        if (regenerate) {
            cache.delete(text, voice_preset);
        }
        // We don't need to re-fetch & re-generate the voiceover if we already have for the text + voice pair
        // Therefore we cache the response in the session storage so that subsequent calls use the cache
        const response = cache.get(text, voice_preset);
        const { url } = response ?? {};
        if (url) {
            shouldFetch = false;
            resolve(url);
        }
        if (shouldFetch) {
            batchConvertTextToVoiceover([text], voice_preset, regenerate, language_code)
                .then(({ urls }) => {
                    resolve(urls[0]);
                })
                .catch((error) => {
                    reject(error);
                });
        }
    });
}

export const createAndUploadVoiceover = async (
    sentences: Array<string>,
    scriptText: string,
    currentAIVoiceId: string,
    language: string,
    processId: string,
) => {
    const { blob, sentence_data } = await createAIVoiceover(
        sentences,
        currentAIVoiceId,
        language,
        processId,
    );
    const audioFile = new File([blob], generateVoiceoverFileName(scriptText, language), {
        type: 'audio/mp3',
    });
    // stitch the elevenlabs transcript
    const script_with_word_timings = concatScriptSentences(sentence_data);
    const audioAsset = await processAIVoiceover(
        audioFile,
        scriptText,
        script_with_word_timings,
        language,
        processId,
        currentAIVoiceId,
    );
    return audioAsset;
};

/**
 * Store voiceover urls in the cache and track character usage
 */
export function processUrls() {
    return (response: BatchTextToVoiceoverResponse): void => {
        const cache = new VoiceoverCache();
        response.blocks.forEach((block) => {
            if (!cache.get(block.text, block.voice_preset)) {
                if (
                    !block.url ||
                    block.script_sentence === null ||
                    block.audible_duration === null
                ) {
                    ErrorReporter.addBreadcrumb({
                        message: 'Block failed to generate voiceover',
                        data: { block },
                    });
                    return;
                }
                cache.set(block.text, block.voice_preset, {
                    url: block.url,
                    sentence_data: {
                        script_sentence: block.script_sentence,
                        audible_duration: block.audible_duration,
                    },
                });
                BatchAnalytics.track(
                    'Elevenlabs Character Usage',
                    {},
                    {
                        num_characters: block.text.length,
                    },
                );
            }
        });
    };
}

function isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
    return value !== null && value !== undefined;
}

export function avatarVideoAsset(avatar: ImmutableMap<AIAvatar>) {
    const previewUrl = avatar.get('preview_url');
    if (!previewUrl) {
        throw new Error(`Avatar ${avatar.get('name')} does not have a preview url`);
    }
    return Immutable.fromJS({
        type: SlideBackgroundModelTypeEnum.VIDEO,
        video: {
            id: uuid(),
            uuid: uuid(),
            source: SourceEnum.SOURCE_AI_AVATAR,
            source_id: uuid(),
            license: VideoData.USER_PRIVATE_LICENSE,
            attribution: null,
            is_editorial: null,
            width: 1080,
            height: 1080,
            thumbnail_url: previewUrl,
            preview_url: previewUrl,
            render_url: null,
            poster_url: avatar.get('thumbnail_url'),
            duration: 10000,
            has_audio: false,
            has_voiceover: true,
            ai_avatar: avatar.get('id'),
            description: null,
            tags: [],
            title: null,
            display_credit_text: null,
            display_credit_url: null,
            captions: null,
        },
        image: null,
        icon: null,
        history_index_user_ids: [],
    }) as unknown as ImmutableMap<SlideBackgroundModel>;
}

export function avatarBackgroundVideoAsset(
    preset: ImmutableMap<AIAvatarVoicePreset> | null,
): MediaSourceData {
    const backgroundPreviewUrl = preset && preset.get('background_preview_url');
    const backgroundRenderUrl = preset && preset.get('background_render_url');

    // Start with placeholder and only override URLs when available
    return {
        ...AVATAR_BACKGROUND_PLACEHOLDER_MEDIA_ASSET_SOURCEDATA,
        ...(backgroundPreviewUrl && { preview_url: backgroundPreviewUrl }),
        ...(backgroundRenderUrl && { render_url: backgroundRenderUrl }),
        ...(backgroundPreviewUrl && { thumbnail_url: backgroundPreviewUrl }),
    };
}

export const generateVoiceoverFileName = (text: string, language: string): string => {
    let wordSeparator = ' ';
    if (language === 'zh' || language === 'ja') {
        // There are no spaces between words in Chinese and Japanese
        // There's also no need to join words using an underscore
        wordSeparator = '';
    }
    // Do some basic sanitizing to remove unwanted characters. Will be further sanitized on upload.
    const sanitizedText = text.replace(/[&/\\#,+()$~%.'":*?<>{}]/g, '');
    // Truncate the file name to the first 10 words not exceeding a 60 char limit
    const fileName = sanitizedText
        .split(wordSeparator)
        .slice(0, 10)
        .join(wordSeparator)
        .slice(0, 60);
    return `${fileName}.mp3`;
};

/**
 * 11labs can generate voiceovers with word timings, but in script composer sentences are unordered blocks.
 * We need to concatenate the blocks and align the word timings of the sentence sequentially.
 */
export const concatScriptSentences = (scriptTranscripts: ScriptSentenceData[]) => {
    const concatenatedTranscript: TranscriptWord[] = [];
    let currentTime = 0;
    scriptTranscripts.forEach((scriptSentenceData) => {
        const { script_sentence, audible_duration } = scriptSentenceData;
        // first adjust the times of each word to the duration of the transcript thus far
        // typescript can't seem to infer script_sentence type from Pick<>, so we cast it here
        const timeAdjustedSentence = (script_sentence as unknown as TranscriptWord[]).map(
            (word) => {
                return {
                    ...word,
                    startTime: word.startTime + currentTime,
                    endTime: word.endTime + currentTime,
                };
            },
        );

        concatenatedTranscript.push(...timeAdjustedSentence);
        // typescript can't seem to infer audible_duration as number from a Pick<> type
        currentTime = currentTime + (audible_duration as unknown as number);
    });

    return concatenatedTranscript;
};

export async function batchConvertTextToVoiceover(
    scriptSentences: Array<string>,
    voicePreset: string,
    regenerate = false,
    language_code: string = UNKNOWN_LANGUAGE_CODE,
): Promise<{ urls: string[]; sentence_data: ScriptSentenceData[] }> {
    const cache = new VoiceoverCache();
    await cache.fetchSentences({
        scriptSentences,
        voicePreset,
        language_code,
        regenerate,
    });

    // Get all the voiceover urls from the cache
    const urls = scriptSentences
        .map((sentence) => cache.get(sentence, voicePreset)?.url)
        .filter(isNotNullOrUndefined);
    const sentence_data = scriptSentences
        .map((sentence) => cache.get(sentence, voicePreset)?.sentence_data)
        .filter(isNotNullOrUndefined);
    return { urls, sentence_data };
}

export async function createAIVoiceover(
    scriptSentences: Array<string>,
    voicePreset: string,
    language: string,
    processId: string | null = null,
): Promise<{ blob: Blob; sentence_data: ScriptSentenceData[] }> {
    const regenerate = false;
    const { urls, sentence_data } = await batchConvertTextToVoiceover(
        scriptSentences,
        voicePreset,
        regenerate,
        language,
    );
    // Sanity check to ensure that we have a url for each sentence. Otherwise, we cannot proceed or we will have an incomplete voiceover
    // This kind of mismatch is usually due to intermittent api errors so it could be resolved if the user tries again.
    if (urls.length !== scriptSentences.length) {
        const error = new Error('Failed to generate all voiceover urls');
        ErrorReporter.capture(error, {
            extra: {
                urls,
                scriptSentences,
            },
        });
        throw error;
    }
    analytics.track('Fetched voiceover urls', {
        urls,
        scriptSentences,
        urlCount: urls.length,
        sentenceCount: scriptSentences.length,
    });
    // concatenate the block level previews into one voiceover track
    const crunker = new Crunker();
    analytics.track('Fetching audio');
    const buffers = await crunker.fetchAudio(...urls);
    analytics.track('Merging audio', { buffers: buffers });
    const merged = crunker.concatAudio(buffers);
    analytics.track('Exporting audio', { buffer: merged });
    const output = crunker.export(merged, 'audio/mp3');
    // Temporarily tracking on mixpanel for better visibility of the generated files on before uploading the voiceover
    analytics.track('AI voiceover stitching complete', {
        scriptSentences,
        urls,
        processId,
    });
    return {
        blob: output.blob,
        sentence_data,
    };
}

export const processAIVoiceover = async (
    audioFile: File,
    script: string,
    script_with_word_timings: TranscriptWord[],
    language: string,
    processId: string | null = null,
    ai_voice: string | undefined = undefined,
): Promise<ImmutableMap<AudioAssetModel>> => {
    const targetLanguage =
        language.toLowerCase() === UNKNOWN_LANGUAGE_CODE || !language ? undefined : language;
    return new Promise<ImmutableMap<AudioAssetModel>>((resolve, reject) => {
        const uploadFailed = (file: FileWithMetadata, message: string, error: Error) => {
            reject(error);
        };

        const uploadSucceeded = (
            file_obj: FileWithMetadata,
            newAsset: ImmutableMap<AssetContainerModel>,
            type: AssetContainerTypes,
        ) => {
            if (!immutableAssetContainerModelIsAudio(newAsset)) {
                reject(new Error('Asset is not an audio'));
                return;
            }
            resolve(newAsset.get(AssetContainerTypes.AUDIO));
        };

        const abortController = createAbortController();
        const media_uploader = new UploadMediaController({
            file_list: [{ file: audioFile }],
            asset_type: AssetContainerTypes.AUDIO,
            metadata: {
                source: SourceEnum.SOURCE_AI_VOICEOVER,
                license: VideoData.USER_PRIVATE_LICENSE,
                script,
                script_with_word_timings,
                language: targetLanguage,
                ai_voice,
            },
            upload_succeeded_callback: uploadSucceeded,
            upload_failed_callback: uploadFailed,
            abort_controller: abortController,
            analyticsContext: {
                trigger: 'AI Voiceover',
            },
        });

        // Throw an error and track if upload process takes longer than 10 minutes, return so that users can try
        const TIMEOUT_FOR_UPLOAD_PROCESS = 600000;
        setTimeout(() => {
            const message = 'Timeout while processing AI voiceover';
            abortController.abort(message);
        }, TIMEOUT_FOR_UPLOAD_PROCESS);

        analytics.track('Initialized voiceover upload', { script, processId });
        media_uploader.upload();
    });
};

export function getEstimatedVoiceoverDuration(text: string): number {
    // TODO: Use language-aware word splitting https://lumen5.atlassian.net/browse/MR-3356
    const wordCount = text.split(' ').length;
    return wordCount * WORD_DURATION_ESTIMATE_SECONDS;
}

export function getOrEstimateVoiceoverDuration(text: string): number {
    text = text.toLowerCase().trim();
    const duration = parseFloat(sessionStorage.getItem(text) ?? 'NaN');
    if (isNaN(duration)) {
        return getEstimatedVoiceoverDuration(text);
    }
    return duration;
}
